import { sortBy } from '@/lib/sortBy';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import {
  ChargeDetailParams,
  ChargesDetailQueries,
  chargesQueries,
  CurrentChargeParams,
  CurrentChargesQueries,
} from '@/services/charges/charges.queries';
import { Charges, CurrentCharges } from '@/services/charges/charges.types';
import { parseDateRangeSearch } from '@/lib/parseDateRange';
import { useSearch } from '@tanstack/react-router';
import { encryptedAgentTypeMap } from '../charge.constant';
import { defaultMeta } from '@/constants/defaultMeta';
import { PaginatedApiResponse } from '@/react-query/context/ApiClientContextController';

const useParams = () => {
  const search = useSearch({ from: '/(app)/configuration-management/charges/' });
  const { page, fetch, agentType, createdDate, sort } = search;
  const defaultAgentType = encryptedAgentTypeMap.affiliate;
  const parsedCreatedDate = parseDateRangeSearch(createdDate, { start: 0, end: 0 });
  return {
    row: page ?? 1,
    agentType: agentType ?? defaultAgentType,
    fetch: fetch ?? 10,
    search: search.q ?? '',
    fromDate: parsedCreatedDate?.start ?? 0,
    toDate: parsedCreatedDate?.end ?? 0,
    sort,
  };
};

type ChargeType = 'club' | 'affiliate' | 'customer';

export const useCharges = (type: ChargeType, enabled: boolean) => {
  const { sort, ...params } = useParams();

  const { data, isLoading } = useQuery<PaginatedApiResponse<Charges[]>>({
    ...chargesQueries.list(type, params),
    enabled,
    select: (res): PaginatedApiResponse<Charges[]> => ({
      ...res,
      data: sortBy(res.data, sort),
    }),
  });

  return {
    charges: data?.data || [],
    isLoading,
    meta: data?.meta?.pagination ?? defaultMeta,
  };
};

export const useCurrentCharges = (enabled: boolean, params: CurrentChargeParams) => {
  const { data } = useQuery({
    ...CurrentChargesQueries.list(params),
    enabled: params.affiliateId !== '' && enabled,
  });

  return { currentCharges: data?.data ?? ({} as CurrentCharges) };
};

export const useChargeDetail = (params: ChargeDetailParams) => {
  const { data, isLoading } = useQuery({
    ...ChargesDetailQueries.list(params),
    enabled: !!(params.agentType && params.chargeId),
  });

  return { chargeDetail: data?.data ?? ({} as ChargeDetailParams), isLoading };
};
